<div class="min-h-screen bg-base-200 p-4">
  <div class="max-w-4xl mx-auto">
    <!-- KAN-468: complete-your-profile prompt (learners with unfilled requested fields) -->
    <app-complete-profile-prompt></app-complete-profile-prompt>
    <!-- Welcome Header -->
    <div class="hero bg-gradient-to-r from-primary to-secondary rounded-box shadow-xl mb-6">
      <div class="hero-content text-center">
        <div class="max-w-md">
          <h1 data-testid="dashboard-welcome-heading" class="text-4xl font-bold mb-4 text-primary-content">
            <app-icon name="person" size="lg" class="text-accent mr-3"></app-icon>
            Welcome to Your Dashboard!
          </h1>
          <p class="text-lg text-primary-content opacity-90">
            Manage your courses, profile, and learning journey all in one place.
          </p>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="card bg-base-100 shadow-xl mb-6">
      <div class="card-body">
        <h2 class="card-title text-2xl mb-4">
          <app-icon name="bolt" size="md" class="text-accent"></app-icon>
          Quick Actions
          <app-help-icon
            feature="quick-actions"
            title="Quick Actions"
            preview="Access your most used features with one click"
            class="ml-2">
          </app-help-icon>
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <a routerLink="/courses" data-testid="dashboard-courses-link" class="btn btn-primary btn-lg h-auto p-4 flex-col gap-2">
            <app-icon name="book" size="lg" class="text-primary-content"></app-icon>
            <span>My Courses</span>
          </a>

          <a routerLink="/bookmarks" data-testid="dashboard-bookmarks-link" class="btn btn-warning btn-lg h-auto p-4 flex-col gap-2">
            <app-icon name="bookmark" size="lg" class="text-warning-content"></app-icon>
            <span>My Bookmarks</span>
          </a>

          @if (currentUser?.currentOrgRole === 'instructor' || currentUser?.currentOrgRole === 'admin' || currentUser?.role === 'instructor') {
            <a
              [routerLink]="'/instructor/organization/' + currentUser?.currentOrgId"
              data-testid="dashboard-organizations-link"
              class="btn btn-secondary btn-lg h-auto p-4 flex-col gap-2">
              <app-icon name="business" size="lg" class="text-secondary-content"></app-icon>
              <span>My Organization</span>
            </a>
          }

          <a routerLink="/profile/edit" data-testid="dashboard-edit-profile-link" class="btn btn-accent btn-lg h-auto p-4 flex-col gap-2">
            <app-icon name="settings" size="lg" class="text-accent-content"></app-icon>
            <span>Edit Profile</span>
          </a>

          @if (currentUser?.isSystemAdmin) {
            <a
              routerLink="/admin/dashboard"
              data-testid="dashboard-admin-link"
              class="btn btn-info btn-lg h-auto p-4 flex-col gap-2">
              <app-icon name="admin_panel_settings" size="lg" class="text-info-content"></app-icon>
              <span>Admin Dashboard</span>
            </a>
          }
        </div>
      </div>
    </div>

    <!-- Due Dates Section -->
    <div class="card bg-base-100 shadow-xl mb-6">
      <div class="card-body">
        <div class="flex justify-between items-center mb-4">
          <h2 class="card-title text-2xl">
            <app-icon name="schedule" size="md" class="text-warning"></app-icon>
            Upcoming Due Dates
            <app-help-icon
              feature="due-dates"
              title="Due Dates"
              preview="Track your course completion deadlines and stay on schedule"
              searchQuery="due dates reminders"
              class="ml-2">
            </app-help-icon>
          </h2>
        </div>

        <!-- Due Date Summary Stats -->
        @if (dueDateSummary) {
          <div data-testid="dashboard-due-date-stats-section" class="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div class="stat place-items-center" data-testid="dashboard-overdue-stat">
              <div class="stat-value text-error text-sm">{{ dueDateSummary.overdue }}</div>
              <div class="stat-title text-xs">Overdue</div>
            </div>
            <div class="stat place-items-center" data-testid="dashboard-due-soon-stat">
              <div class="stat-value text-warning text-sm">{{ dueDateSummary.dueSoon }}</div>
              <div class="stat-title text-xs">Due Soon</div>
            </div>
            <div class="stat place-items-center" data-testid="dashboard-due-this-week-stat">
              <div class="stat-value text-info text-sm">{{ dueDateSummary.dueThisWeek }}</div>
              <div class="stat-title text-xs">This Week</div>
            </div>
            <div class="stat place-items-center" data-testid="dashboard-on-track-stat">
              <div class="stat-value text-success text-sm">{{ dueDateSummary.onTrack }}</div>
              <div class="stat-title text-xs">On Track</div>
            </div>
            <div class="stat place-items-center" data-testid="dashboard-total-stat">
              <div class="stat-value text-base-content text-sm">{{ dueDateSummary.total }}</div>
              <div class="stat-title text-xs">Total</div>
            </div>
          </div>
        }

        <!-- Loading State -->
        @if (isLoadingDueDates) {
          <div data-testid="dashboard-due-dates-loading" class="text-center py-4">
            <span class="loading loading-spinner loading-md"></span>
            <p class="mt-2 text-sm text-base-content opacity-70">Loading due dates...</p>
          </div>
        }

        <!-- Error State -->
        @if (dueDatesError) {
          <div data-testid="dashboard-due-dates-error-alert" class="alert alert-error">
            <app-icon name="error" size="sm"></app-icon>
            <span>{{ dueDatesError }}</span>
          </div>
        }

        <!-- Due Dates List -->
        @if (!isLoadingDueDates && !dueDatesError && dueDates && dueDates.length > 0) {
          <div data-testid="dashboard-due-dates-list" class="space-y-3">
            @for (item of dueDates; track item) {
              <div [attr.data-testid]="'dashboard-due-date-' + item.courseId + '-item'" class="flex items-center justify-between p-4 bg-base-200 rounded-lg">
                <div class="flex-1">
                  <h4 class="font-semibold">{{ item.courseTitle }}</h4>
                  <p class="text-sm text-base-content opacity-70">Due: {{ formatDate(item.completionDueDate) }}</p>
                </div>
                <div class="flex items-center gap-3">
                  <div class="badge" [class]="getDueStatusBadgeClass(item.dueStatus)">
                    {{ getDueStatusText(item.dueStatus, item.daysUntilDue) }}
                  </div>
                  @if (item.isCompleted) {
                    <div class="badge badge-success">
                      <app-icon name="check" size="xs"></app-icon>
                      Completed
                    </div>
                  }
                  <a [routerLink]="['/courses', item.courseId]" class="btn btn-sm btn-primary">
                    <app-icon name="play_arrow" size="sm"></app-icon>
                    Continue
                  </a>
                </div>
              </div>
            }
          </div>
        }

        <!-- Empty State -->
        @if (!isLoadingDueDates && !dueDatesError && dueDates && dueDates.length === 0) {
          <div data-testid="dashboard-due-dates-empty" class="text-center py-8">
            <app-icon name="event_available" size="xl" class="text-base-content opacity-20 mb-4"></app-icon>
            <p class="text-lg text-base-content opacity-70 mb-2">No upcoming due dates</p>
            <p class="text-base-content opacity-50">All caught up! Great work.</p>
          </div>
        }
      </div>
    </div>

    <!-- Courses Section (learner only — instructors/admins use Instructor Tools > Courses) -->
    @if (!isInstructor && !isAdmin) {
    <div class="card bg-base-100 shadow-xl">
      <div class="card-body">
        <!-- KAN-632: wraps so "View All" drops onto its own line at phone widths
             instead of poking past the panel edge. -->
        <div class="flex flex-wrap justify-between items-center gap-2 mb-4">
          <h2 class="card-title text-2xl">
            <app-icon name="school" size="md" class="text-success"></app-icon>
            Your Courses
            <app-help-icon
              feature="my-courses"
              title="My Courses"
              preview="View and manage your enrolled courses"
              articleId="getting-started"
              class="ml-2">
            </app-help-icon>
          </h2>
          @if (courses.length > 0) {
            <a routerLink="/courses" class="btn btn-sm btn-outline">
              View All
            </a>
          }
        </div>

        <!-- Loading State -->
        @if (isLoadingCourses) {
          <div data-testid="dashboard-courses-loading" class="text-center py-4">
            <span class="loading loading-spinner loading-md"></span>
            <p class="mt-2 text-sm text-base-content opacity-70">Loading courses...</p>
          </div>
        }

        <!-- Error State -->
        @if (coursesError) {
          <div data-testid="dashboard-courses-error-alert" class="alert alert-error">
            <app-icon name="error" size="sm"></app-icon>
            <span>{{ coursesError }}</span>
          </div>
        }

        <!-- Courses List -->
        @if (!isLoadingCourses && !coursesError && courses.length > 0) {
          <div data-testid="dashboard-courses-list" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            @for (course of courses; track course) {
              <div
                [attr.data-testid]="'dashboard-course-' + course.id + '-card'"
                class="card bg-base-200 shadow hover:shadow-lg transition-all cursor-pointer"
                (click)="navigateToCourse(course)">
                <div class="card-body p-4">
                  <!-- KAN-632: below sm the avatar and chevron share the top row and the
                       text block spans the whole card underneath them, so names and the
                       organization wrap (2-line clamp) instead of truncating to one
                       character and the progress bar spans the card. sm and up keep the
                       side-by-side row: avatar | text | chevron. -->
                  <div class="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 gap-y-3 items-start">
                    <!-- Course Avatar -->
                    <div class="avatar placeholder col-start-1 row-start-1">
                      <div class="bg-primary text-primary-content rounded-lg w-12 h-12">
                        <span class="text-lg font-bold">{{ getCourseInitials(course.title || course.name || '') }}</span>
                      </div>
                    </div>
                    <div class="min-w-0 col-span-3 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1">
                      <div class="flex flex-wrap items-center gap-x-2 sm:flex-nowrap">
                        <h4 class="font-semibold min-w-0 line-clamp-2 [overflow-wrap:anywhere] sm:line-clamp-none sm:truncate" [title]="course.title || course.name">{{ course.title || course.name }}</h4>
                        <span class="text-xs font-mono text-base-content opacity-40 flex-shrink-0">#{{ course.id.slice(-4) }}</span>
                      </div>
                      <p class="text-sm text-base-content opacity-70 line-clamp-2 [overflow-wrap:anywhere] sm:line-clamp-none sm:truncate" [title]="course.organizationName || 'Unknown Organization'">{{ course.organizationName || 'Unknown Organization' }}</p>
                      <!-- Progress Bar -->
                      <div class="mt-2">
                        <div class="flex justify-between items-center gap-2 text-xs mb-1">
                          <span class="badge badge-sm whitespace-nowrap shrink-0" [class]="getStatusBadgeClass(course)">{{ getStatusText(course) }}</span>
                          <span class="opacity-70 whitespace-nowrap shrink-0">{{ getProgressPercentage(course) }}%</span>
                        </div>
                        <progress
                          class="progress progress-primary w-full h-2"
                          [value]="getProgressPercentage(course)"
                          max="100">
                        </progress>
                      </div>
                    </div>
                    <!-- Action Arrow -->
                    <app-icon name="chevron_right" size="sm" class="text-base-content opacity-50 col-start-3 row-start-1 justify-self-end"></app-icon>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        <!-- Empty State -->
        @if (!isLoadingCourses && !coursesError && courses.length === 0) {
          <div data-testid="dashboard-courses-empty" class="text-center py-8">
            <app-icon name="description" size="xl" class="text-base-content opacity-20 mb-4"></app-icon>
            <p class="text-lg text-base-content opacity-70 mb-4">No courses available yet!</p>
            <p class="text-base-content opacity-50">Check back later or contact an administrator.</p>
          </div>
        }
      </div>
    </div>
    }

    <!-- Role-Adaptive Instructor Section -->
    @if (isInstructor) {
      <div data-testid="dashboard-instructor-section" class="card bg-base-100 shadow-xl mb-6">
        <div class="card-body">
          <!-- KAN-618: below md the org label stacks under the heading and wraps,
               so it reads in full and never lands between two heading lines. -->
          <div class="flex flex-col gap-1 mb-4 md:flex-row md:justify-between md:items-center md:gap-4">
            <h2 class="card-title text-2xl md:whitespace-nowrap md:shrink-0">
              <app-icon name="school" size="md" class="text-secondary"></app-icon>
              Instructor Tools
            </h2>
            @if (organizationContext) {
              <div data-testid="dashboard-instructor-org-label"
                   class="text-sm text-base-content opacity-70 user-text-wrap md:text-right"
                   [title]="organizationContext.name + ' • ' + (organizationContext.role | titlecase)">
                {{ organizationContext.name }} • {{ organizationContext.role | titlecase }}
              </div>
            }
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Content Hub (Primary) -->
            <a routerLink="/instructor/content-hub"
              data-testid="dashboard-content-hub-link"
              class="card bg-gradient-to-br from-primary to-primary-focus text-primary-content shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div class="card-body items-center text-center p-6">
                <app-icon name="hub" size="xl" class="mb-3"></app-icon>
                <h3 class="card-title text-lg">Content Hub</h3>
                <p class="text-sm opacity-80">Unified content management</p>
                <span class="badge badge-accent mt-2">Recommended</span>
              </div>
            </a>
            <!-- Course Management -->
            <a routerLink="/courses"
              data-testid="dashboard-instructor-courses-link"
              class="card bg-base-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div class="card-body items-center text-center p-6">
                <app-icon name="library_books" size="xl" class="text-secondary mb-3"></app-icon>
                <h3 class="card-title text-lg">Courses</h3>
                <p class="text-sm text-base-content opacity-70">Manage your courses</p>
              </div>
            </a>
            <!-- Gradebook -->
            <a routerLink="/instructor/gradebook"
              data-testid="dashboard-gradebook-link"
              class="card bg-base-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div class="card-body items-center text-center p-6">
                <app-icon name="grading" size="xl" class="text-success mb-3"></app-icon>
                <h3 class="card-title text-lg">Gradebook</h3>
                <p class="text-sm text-base-content opacity-70">Track student progress</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    }

    <!-- Role-Adaptive Admin Section -->
    @if (isAdmin) {
      <div data-testid="dashboard-admin-section" class="card bg-base-100 shadow-xl mb-6">
        <div class="card-body">
          <h2 class="card-title text-2xl mb-4">
            <app-icon name="admin_panel_settings" size="md" class="text-error"></app-icon>
            Administrator Tools
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <a routerLink="/admin/organizations"
              class="card bg-base-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div class="card-body items-center text-center p-4">
                <app-icon name="business" size="lg" class="text-info mb-2"></app-icon>
                <h4 class="text-sm font-semibold">Organizations</h4>
              </div>
            </a>
            <a routerLink="/admin/users"
              class="card bg-base-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div class="card-body items-center text-center p-4">
                <app-icon name="group" size="lg" class="text-info mb-2"></app-icon>
                <h4 class="text-sm font-semibold">Users</h4>
              </div>
            </a>
            <a routerLink="/admin/system"
              class="card bg-base-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div class="card-body items-center text-center p-4">
                <app-icon name="settings" size="lg" class="text-info mb-2"></app-icon>
                <h4 class="text-sm font-semibold">System</h4>
              </div>
            </a>
            <a routerLink="/admin/reports"
              class="card bg-base-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div class="card-body items-center text-center p-4">
                <app-icon name="assessment" size="lg" class="text-info mb-2"></app-icon>
                <h4 class="text-sm font-semibold">Reports</h4>
              </div>
            </a>
          </div>
        </div>
      </div>
    }
  </div>
</div>
